import { useQueryClient, useQueryErrorResetBoundary } from '@tanstack/react-query'
import { Link, useRouter, type ErrorComponentProps } from '@tanstack/react-router'
import { useEffect, useRef, useState, type ReactNode } from 'react'

import { OfflineError } from '@/app/query-client'
import ScreenLayout from '@/components/layout/ScreenLayout'
import { useInShell } from '@/components/layout/shell-context'
import EmptyState from '@/components/ui/EmptyState'
import { stateActionClass, stateCopy } from '@/components/ui/empty-state-variants'
import { useOnline } from '@/hooks/useOnline'

export interface RouteErrorPageProps extends ErrorComponentProps {
  /** The route's own title row, so a failed page still says where it is. */
  header?: ReactNode
  /** The failed state's words. Offline keeps EmptyState's own. */
  title?: string
  description?: string
  /** rest:ln — the page draws no rail (ScreenLayout `fullWidth`), so its failed state has none. */
  fullWidth?: boolean
}

/**
 * The router's `defaultErrorComponent`, and the base every route's own
 * `errorComponent` is built on: the page's MAIN data did not load, so there
 * is no page — one whole-page failed (or offline) state instead.
 *
 * Only the first load lands here. A loader throws when `ensureRouteData`
 * rejects, and a screen's `useSuspenseQuery` throws only while it has no data
 * (its `throwOnError` is `data === undefined`) — so a poll or a focus refetch
 * that fails after the page drew keeps the page, and says so through the
 * screen's RefreshBar. Secondary cards never reach this: their errors stay
 * card-local.
 *
 * Retry refetches the query that failed FIRST, and only then re-runs the
 * route — never the other way round. The router draws a failed match's
 * pending component with no delay the moment it re-runs it, so re-running
 * straight away would swap this state for the skeleton at once and, if the
 * attempt failed again, back — taking the member's focus off the button they
 * pressed. This way the button reads "Trying…" for the whole attempt, the
 * same contract QueryState keeps, and a failed attempt leaves everything
 * where it was. A successful one still passes through the skeleton on its
 * way to the page (that no-delay rule again; the skeleton's own 80ms wait is the only hold after
 * it); the router offers no way to skip it. The failed query is found by
 * its error: the loader rethrows the very error the query holds. An error no
 * query holds re-runs the route the same way — UNLESS it is offline (or an
 * `OfflineError`), which instead does nothing and leaves the retry to the
 * reconnect effect below; see `retry`.
 *
 * `router.load()` rather than `router.invalidate()`: it re-runs only the
 * loader that failed, not every loader in the branch. The boundary's own
 * `reset()` follows, for the case where the page's component threw rather
 * than its loader — there is then no failed match to re-run.
 *
 * The query error boundary is reset on mount — TanStack's documented pairing
 * — so a suspense query that threw here may fetch again when the page
 * remounts, rather than rethrowing its cached error (`retryOnMount` is off
 * for a query whose boundary has not been reset).
 *
 * An offline failure also retries by itself when the network comes back —
 * the loaded page would have, through React Query's `refetchOnReconnect`,
 * and this page has no mounted query to do it. That is also why "Try
 * again" is a no-op while offline (or the error itself is `OfflineError`):
 * see `retry` below.
 *
 * Inside the shell it keeps the page's frame (ScreenLayout, rail and all);
 * outside it — the auth and onboarding routes, the root — it is the whole
 * viewport and carries its own way home. See shell-context.ts.
 */
export default function RouteErrorPage({
  error,
  reset: resetBoundary,
  header,
  title,
  description,
  fullWidth = false,
}: RouteErrorPageProps) {
  const router = useRouter()
  const queryClient = useQueryClient()
  const inShell = useInShell()
  const online = useOnline()
  const { reset } = useQueryErrorResetBoundary()
  const [retrying, setRetrying] = useState(false)

  useEffect(() => {
    reset()
  }, [reset])

  const offline = error instanceof OfflineError || !online

  // An `OfflineError` is thrown before any query runs, so it matches none.
  const failedQueries = () =>
    queryClient.getQueryCache().findAll({ predicate: (query) => query.state.error === error })

  const retry = () => {
    // Offline: `router.load()` would re-run the loader, which redraws this
    // page's pending component with NO delay (the docstring above explains
    // why) — a skeleton flash for an attempt that is going to fail the same
    // way while the network is down. Measured: pending at 26ms, the error
    // again at 461ms, and focus dropped to <body> in between, because the
    // "Try again" button the member had just pressed is unmounted for the
    // length of that round trip. Offline already retries itself, through
    // the reconnect effect below, the moment the network comes back — so
    // there is nothing productive to do here. Leaving early, before
    // `setRetrying` runs at all, keeps this a true no-op: the button never
    // re-renders, so it never has a focus, a label or a place to lose.
    if (offline) return
    setRetrying(true)
    void (async () => {
      try {
        const failed = failedQueries()
        if (failed.length > 0) {
          await queryClient.refetchQueries({ predicate: (query) => failed.includes(query) })
          if (failed.some((query) => query.state.data === undefined)) return
        }
        await router.load()
        resetBoundary()
      } finally {
        setRetrying(false)
      }
    })()
  }

  // A transition from offline to online, not "is online": a failure raised
  // while `navigator.onLine` still said true would otherwise retry on mount,
  // fail the same way and remount — forever.
  const wasOnline = useRef(online)
  useEffect(() => {
    const cameBack = online && !wasOnline.current
    wasOnline.current = online
    if (cameBack) void router.load()
  }, [online, router])

  if (!inShell) {
    return (
      <main className="flex min-h-dvh items-center justify-center px-4 py-10">
        <EmptyState
          kind={offline ? 'offline' : 'failed'}
          size="page"
          headingLevel="h1"
          title={offline ? undefined : (title ?? 'Something went wrong')}
          description={offline ? undefined : (description ?? stateCopy('failed').description)}
          onRetry={retry}
          retrying={retrying}
          secondaryAction={
            <Link to="/" className={stateActionClass('page', 'secondary')}>
              Go home
            </Link>
          }
        />
      </main>
    )
  }

  // "Saved at …" dates the last copy the failed query fetched, the rule every offline state
  // follows. Only a page's first load lands here, so there is usually none and the line stays
  // out; there is no "Show last close" for the same reason — nothing saved to show.
  const savedAt = offline
    ? Math.max(0, ...failedQueries().map((query) => query.state.dataUpdatedAt))
    : undefined

  return (
    <ScreenLayout header={header} fullWidth={fullWidth}>
      <EmptyState
        kind={offline ? 'offline' : 'failed'}
        size="page"
        title={offline ? undefined : title}
        description={offline ? undefined : description}
        savedAt={savedAt}
        onRetry={retry}
        retrying={retrying}
      />
    </ScreenLayout>
  )
}
